<template>
  <DefaultLayout>
    <div class="mx-auto mt-6 w-full max-w-[1400px] px-4">
      <PageHeader
        :to="{ name: 'user-profile' }"
        title="Detalhes do usuário"
        subtitle="Informações e uso do colaborador" />
      <div class="border border-stroke bg-white shadow-sm">

      <div v-if="loading" class="p-8 text-center text-slate-500">Carregando...</div>
      <div v-else-if="error" class="p-8 text-center text-red-600">{{ error }}</div>

      <template v-else-if="user">
        <div class="grid grid-cols-1 md:grid-cols-3">
          <section class="border-b border-stroke p-6 md:col-span-1 md:border-b-0 md:border-r">
            <div class="flex items-center gap-4">
              <div
                class="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-2xl font-bold text-slate-600">
                {{ userInitial(user.name) }}
              </div>
              <div>
                <h2 class="text-xl font-semibold text-gray-800">{{ user.name }}</h2>
                <p class="text-sm text-gray-500">{{ translateRole(user.role) }}</p>
              </div>
            </div>
          </section>

          <section class="p-6 md:col-span-2">
            <h3 class="text-lg font-semibold text-slate-900">Informações e uso</h3>
            <div class="mt-4 space-y-4">
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</p>
                  <p class="mt-1 text-sm text-slate-700">{{ user.email || '-' }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Telefone</p>
                  <p class="mt-1 text-sm text-slate-700">{{ user.phone_number || '-' }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Recebe alertas</p>
                  <p class="mt-1 text-sm text-slate-700">{{ user.receive_alerts ? 'Sim' : 'Não' }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Total de acessos</p>
                  <p class="mt-1 text-sm text-slate-700">{{ user.sign_in_count ?? '-' }}</p>
                </div>
              </div>

              <div class="border-t border-stroke pt-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Último acesso</p>
                <p class="mt-1 text-sm text-slate-700">{{ formatDate(user.last_sign_in_at) }}</p>
              </div>
              <div>
                <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Acesso atual (sessão)</p>
                <p class="mt-1 text-sm text-slate-700">{{ formatDate(user.current_sign_in_at) }}</p>
              </div>
            </div>
          </section>
        </div>
      </template>
      </div>
    </div>
  </DefaultLayout>
</template>

<script setup lang="ts">
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import PageHeader from '@/components/Globals/PageHeader.vue'
import UserResource from '@/api/resources/user'
import type { ICompanyUserDetail } from '@/interfaces/entities/user'
import { translateUserRole } from '@/interfaces/enums'
import { useRoute } from 'vue-router'
import { ref, onMounted } from 'vue'

const route = useRoute()
const user = ref<ICompanyUserDetail | null>(null)
const loading = ref(true)
const error = ref('')

const userInitial = (name?: string) => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return name[0]?.toUpperCase() ?? '?'
}

const translateRole = (role?: string) => translateUserRole(role)

const formatDate = (date?: string | null) => {
  if (!date) return 'Não informado'
  return new Date(date).toLocaleString('pt-BR')
}

onMounted(async () => {
  const userId = route.params.userId
  const companyId = route.params.profileCompanyId
  if (!userId || !companyId) {
    error.value = 'Parâmetros inválidos'
    loading.value = false
    return
  }
  try {
    const data = await UserResource.getCompanyUserDetail(Number(companyId), Number(userId))
    user.value = data
  } catch (e: any) {
    error.value = e?.response?.data?.errors || 'Não foi possível carregar os dados do usuário.'
  } finally {
    loading.value = false
  }
})
</script>
